import {
  PiDotsSixVertical,
  PiLock,
  PiPencilSimple,
  PiTrash,
} from 'react-icons/pi'
import {
  forwardRef,
  type CSSProperties,
  type HTMLAttributes,
  type Ref,
} from 'react'
import { useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { cn } from 'components/UI/cn'
import { FloatingTooltip } from 'components/Tooltip/FloatingTooltip'
import { SalesStage } from 'models/sales_stages'

/** Row action affordances (edit + staged delete) shown on the far right. */
interface StageRowActionProps {
  /** Opens the edit-stage modal for this stage (hands off like create). */
  onEdit?: () => void
  /** Stage the deletion targets (removes it from the list; commits on Save). */
  onDelete?: () => void
  /** When false, the trash icon is disabled and explains why via tooltip. */
  canDelete?: boolean
  /** Dark-tooltip copy shown when `canDelete` is false (e.g. "still used by…"). */
  deleteDisabledReason?: string
}

export interface StageDragAndDropItemProps extends StageRowActionProps {
  id: number
  stage: SalesStage
  /** Tailwind class for the leading stage swatch dot (e.g. a getStageColor dot). */
  dotClassName?: string
  /** Locked rows (terminal stages) render muted with a lock icon, no grip. */
  locked?: boolean
}

interface StageRowViewProps
  extends HTMLAttributes<HTMLDivElement>, StageRowActionProps {
  stage: SalesStage
  dotClassName?: string
  locked?: boolean
  /** Rendered inside the DragOverlay — lifted card treatment, no hover. */
  overlay?: boolean
  /** The placeholder left in the list while its overlay copy is dragged. */
  ghost?: boolean
  /** Ref + props wired to the grip handle (the drag activator). */
  handleRef?: Ref<HTMLButtonElement>
  handleProps?: HTMLAttributes<HTMLButtonElement>
}

/**
 * Presentational sales-stage row. Pure markup so it can be reused both as the
 * registered sortable row and as the floating DragOverlay copy without
 * double-registering the same sortable id (which is what made the dragged
 * element jump off to the side before settling).
 */
const StageRowView = forwardRef<HTMLDivElement, StageRowViewProps>(
  function StageRowView(
    {
      stage,
      dotClassName,
      locked = false,
      overlay = false,
      ghost = false,
      handleRef,
      handleProps,
      onEdit,
      onDelete,
      canDelete = true,
      deleteDisabledReason,
      className,
      ...rest
    },
    ref
  ) {
    // Row actions only make sense on live, regroupable rows — never on the
    // floating drag copy or the locked terminal outcomes.
    const showActions =
      (Boolean(onEdit) || Boolean(onDelete)) && !overlay && !locked
    return (
      <div
        ref={ref}
        className={cn(
          'flex items-center gap-3 rounded-lg border bg-white px-3 py-2.5 transition-colors',
          locked
            ? 'border-gray-200 bg-gray-50'
            : 'border-gray-200 hover:border-gray-300',
          overlay && 'border-brand-bright shadow-lg',
          ghost && 'opacity-40',
          className
        )}
        {...rest}
      >
        {locked ? (
          <PiLock size={15} className="flex-shrink-0 text-gray-400" />
        ) : (
          <button
            ref={handleRef}
            type="button"
            aria-label={`Drag to reorder or regroup ${stage.stage}`}
            className={cn(
              'flex-shrink-0 text-gray-400 hover:text-gray-600 touch-none',
              overlay ? 'cursor-grabbing' : 'cursor-grab active:cursor-grabbing'
            )}
            {...handleProps}
          >
            <PiDotsSixVertical size={17} />
          </button>
        )}

        {dotClassName && (
          <span
            className={cn('size-2.5 flex-shrink-0 rounded-full', dotClassName)}
            aria-hidden
          />
        )}

        <span className="min-w-0 flex-1 truncate text-sm font-medium tracking-[-0.2px] text-gray-800">
          {stage.stage}
        </span>

        {stage.win_rate !== null && (
          <span className="flex-shrink-0 text-xs text-gray-500">
            {stage.win_rate}% win rate
          </span>
        )}

        {showActions && (
          <>
            {/* Hairline divider fencing the far-right action cluster off from
                the win rate (or the name when there's no win rate). */}
            <span className="h-4 w-px flex-shrink-0 bg-gray-200" aria-hidden />
            {onEdit && (
              <button
                type="button"
                onClick={onEdit}
                aria-label={`Edit ${stage.stage}`}
                className="flex-shrink-0 rounded p-0.5 text-gray-400 transition-colors hover:text-brand-bright"
              >
                <PiPencilSimple size={15} />
              </button>
            )}
            {onDelete &&
              (canDelete ? (
                <button
                  type="button"
                  onClick={onDelete}
                  aria-label={`Delete ${stage.stage}`}
                  className="flex-shrink-0 rounded p-0.5 text-gray-400 transition-colors hover:text-error"
                >
                  <PiTrash size={15} />
                </button>
              ) : (
                <FloatingTooltip
                  theme="dark"
                  text={deleteDisabledReason ?? 'This stage is in use.'}
                >
                  <span
                    aria-label={`Can't delete ${stage.stage}`}
                    className="flex-shrink-0 cursor-not-allowed p-0.5 text-gray-300"
                  >
                    <PiTrash size={15} />
                  </span>
                </FloatingTooltip>
              ))}
          </>
        )}
      </div>
    )
  }
)

/**
 * A single draggable sales-stage row used inside the grouped reorder modal.
 * DS2026 styling: white card, hairline border, phase swatch, grip handle.
 */
export function StageDragAndDropItem({
  id,
  stage,
  dotClassName,
  locked = false,
  onEdit,
  onDelete,
  canDelete,
  deleteDisabledReason,
}: StageDragAndDropItemProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id, disabled: locked })

  const style: CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
  }

  return (
    <StageRowView
      ref={setNodeRef}
      style={style}
      stage={stage}
      dotClassName={dotClassName}
      locked={locked}
      ghost={isDragging}
      handleRef={setActivatorNodeRef}
      handleProps={{ ...attributes, ...listeners }}
      onEdit={onEdit}
      onDelete={onDelete}
      canDelete={canDelete}
      deleteDisabledReason={deleteDisabledReason}
    />
  )
}

/**
 * Floating copy rendered inside <DragOverlay>. Presentational only — it must
 * NOT call useSortable, or the dragged id gets registered twice and the
 * overlay mispositions.
 */
export function StageDragOverlayItem({
  stage,
  dotClassName,
}: {
  stage: SalesStage
  dotClassName?: string
}) {
  return <StageRowView stage={stage} dotClassName={dotClassName} overlay />
}

/**
 * Static, non-draggable row for terminal stages (Closed Won / Lost / Churn).
 * Presentational only — these outcomes can't be regrouped, so they stay out of
 * the drag-and-drop system entirely.
 */
export function StageLockedRow({ stage }: { stage: SalesStage }) {
  return <StageRowView stage={stage} locked />
}
